/* Componente de cadastro de artigo, que é utilizado na página de cadastro de artigo (PaginaArtigo).
    Este componente é referenciado na configuração de rotas em src/routes.js    
    Componente responsável por renderizar o formulário de cadastro de artigo, onde os usuários poderão inserir as informações do artigo que desejam cadastrar. O formulário pode incluir campos como título, conteúdo, autor, data de publicação, entre outros. O componente também pode incluir validação dos campos e lógica para enviar os dados para o backend para serem salvos no banco de dados.
    Formulário será utilizado para criar novos artigos e também para editar artigos existentes, permitindo que os usuários atualizem as informações do artigo conforme necessário.
    Utiliza o hook useLocation para acessar o estado passado pela rota, que pode conter os dados do usuário logado permitindo a inclusão ou edição, caso contrário, o formulário não é exibido. 
*/
import { useEffect } from "react";
import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom";
import CampoTexto from "../CampoTexto";
import Botao from "../Botao";   
import ReactQuill from "react-quill-new"; // Importa o componente de editor de texto rico React Quill para o campo de conteúdo do artigo
import 'react-quill-new/dist/quill.snow.css'; // Importa o estilo do editor React Quill para garantir que o editor seja exibido corretamente com os estilos adequados.
import Mensagem from "../Mensagem";
import { useAuth } from "../../contextos/AuthContext"; // Importa o hook useAuth do contexto de autenticação para acessar o estado do usuário logado e funções relacionadas à autenticação
import './CadastroArtigo.css'; // Importa o arquivo de estilo CSS específico para o componente de cadastro de artigo, permitindo a personalização da aparência do formulário e dos elementos relacionados ao cadastro de artigo.


const CadastroArtigo = () => {
    const location = useLocation(); // Hook para acessar o estado passado pela rota, que pode conter os dados do usuário logado para edição
    const navigate = useNavigate(); // Hook para navegação programática 
    const { dadosUsuario } = useAuth(); // Hook para acessar o contexto de autenticação e obter os dados do usuário logado
    const {token} = useAuth(); // Hook para acessar o contexto de autenticação e obter o token do usuário logado
    const artigo = location.state?.artigo || null; // Verifica se há um artigo passado pela rota para edição, caso contrário, é nulo. Se artigo for nulo, o formulário será utilizado para criar um novo artigo. Se artigo tiver dados, o formulário será preenchido com os dados do artigo para edição.
    console.log("Usuario logado no CadastroArtigo:", dadosUsuario); // Log para verificar o conteúdo de usuarioLogado
    console.log("Artigo recebido para edição no CadastroArtigo:", artigo); // Log para verificar o conteúdo de artigo recebido para edição  
    
    const [obrigatorio, setObrigatorio] = useState(true); // State para controlar se os campos são obrigatórios ou não, inicialmente definido como true para novos cadastros

    const [editavel, setEditavel] = useState(true); // State para controlar se os campos são editáveis ou não, inicialmente definido como true para novos cadastros
    
    const [showToast, setShowToast] = useState(false); // State para controlar a exibição do componente de mensagem (toast) para feedback ao usuário, inicialmente definido como false para não exibir a mensagem

    const [mensagem, setMensagem] = useState({ texto: '', tipo: '' });//Controla o state dos dados do componente Mensagem para exibir feedback ao usuário. Tipo recebe o status da resposta da API para definir o estilo da mensagem (ex: sucesso, erro, aviso) para aplicar estilo conforme o tipo recebido, conforme o componente mensagem .

    const [carregando, setCarregando] = useState(false);//Para controlar o state de carregamento durante a requisição

    const [titulo, setTitulo] = useState(artigo?.titulo || ''); // State para controlar o valor do campo de título do artigo
    const [capaArtigo, setCapaArtigo] = useState(null); // State para controlar o arquivo de capa do artigo, inicialmente definido como null para novos cadastros e também para edição pois caso o usuário queira editar o artigo vai precisar carregar o arquivo da capa novamente.
    
    const tratarArquivo = (evento) => {
        // O arquivo fica dentro de uma lista chamada 'files'
        const arquivoSelecionado = evento.target.files[0]; 

            if (arquivoSelecionado) {
                //Valida se é um arquivo de imagem (recomendado para garantir que o usuário selecione um arquivo válido para a capa do artigo)
                if (!arquivoSelecionado.type.startsWith('image/')) {
                    alert("Por favor, selecione apenas imagens (JPG, PNG).");
                    return;
                }

                // Validar tamanho (ex: 2MB)
                if (arquivoSelecionado.size > 2 * 1024 * 1024) {
                    alert("A imagem deve ter no máximo 2MB.");
                    // 1. Limpa o estado do React
                    setCapaArtigo(null); 
                    // 2. Limpa o "texto" que aparece no campo (O caminho do arquivo)
                    evento.target.value = "";
                    return;
                }

                // Verificar se o arquivo está disponível (size 0 pode indicar que o arquivo ainda está sendo sincronizado ou não está acessível)
                if (arquivoSelecionado && arquivoSelecionado.size === 0) {
                    alert("O arquivo parece estar indisponível ou ainda sincronizando. Tente novamente em instantes.");
                    evento.target.value = "";
                    return;
                }


                setCapaArtigo(arquivoSelecionado);
                console.log("Arquivo pronto para envio:", arquivoSelecionado.name);
            }

};

    /*State para controlar o valor do campo de data de publicação do artigo.
      Setamos o valor inicial para a data e hora atual formatada como 'YYYY-MM-DDTHH:mm', que é o formato esperado pelo input do tipo datetime-local. Isso garante que, ao abrir o formulário de cadastro de artigo, o campo de data de publicação já esteja preenchido com a data e hora atual, facilitando o processo de criação do artigo para os usuários. O usuário pode então ajustar a data e hora conforme necessário antes de publicar o artigo.
      
      Por que o .slice(0, 16)?
        new Date().toISOString() retorna algo como: 2026-04-19T13:25:30.000Z.
        O campo datetime-local só aceita até os minutos: 2026-04-19T13:25.
        O slice(0, 16) corta exatamente os primeiros 16 caracteres, deixando a data pronta para o input.  
        Usamos getTimezoneOffset para descartar o fuso horário local e garantir que a data exibida seja a data e hora local do usuário, em vez de UTC, que é o formato padrão retornado por toISOString(). Isso é importante para evitar confusões com a data e hora exibida no formulário, garantindo que os usuários vejam a data e hora corretas de acordo com seu fuso horário local.   

        Importante: Se o cadastro do artigo for chamado atravpara edição a data assumirá um novo valor considerando a data da edição, e não a data original do artigo. 

    */ 

    const [dataPublicacao, setDataPublicacao] = useState( ()=>{
           const agora = new Date(); 
           agora.setMinutes(agora.getMinutes() - agora.getTimezoneOffset());
    return agora.toISOString().slice(0, 16);
    });
        
 

    const [conteudo, setConteudo] = useState(artigo?.conteudo || ''); // Estado que guarda o HTML do editor

    // Configuração da barra de ferramentas (Toolbar)
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            [{ 'size': ['small', false, 'large', 'huge'] }], // Adiciona o seletor de tamanho
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote'], // 👈 Adiciona o botão de Citação Oficial
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link', 'image'],
            // Alinhamento (O array vazio [] significa "esquerda" por padrão)
            [{ 'align': [] }, { 'align': 'center' }, { 'align': 'right' }, { 'align': 'justify' }],
    
            // Identação (Recuo)
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            ['clean'] // Botão para remover a formatação
        ],
        
    };

// Esta função useEffect é responsável por verificar se o usuário está logado ao acessar o componente de cadastro de artigo. Se o usuário não estiver logado, ele será redirecionado para a página de resultado com uma mensagem de erro informando que é necessário estar logado e ter permissões adequadas para acessar a funcionalidade de cadastro de artigo. O useEffect é utilizado para garantir que essa verificação seja feita sempre que o componente for renderizado ou quando o estado do usuário logado mudar, garantindo assim a segurança e a restrição de acesso à funcionalidade de cadastro de artigo apenas para usuários autenticados e autorizados.
useEffect(() => {
    if (!dadosUsuario) {
        navigate('/resultado', { 
                    state: { 
                    status: "erro",
                    mensagem: "Para editar artigos, você precisa estar logado e ser administrador ou editor."
                          }
                    });   
                    return; // Encerra a função se o usuário  não for encontrado 
    }

    const dadosArtigo = JSON.parse(localStorage.getItem("DadosArtigo"));

    if (dadosArtigo) {
        setTitulo(dadosArtigo.titulo);
        setDataPublicacao(dadosArtigo.dataPublicacao);
        setConteudo(dadosArtigo.conteudo);
    }

}, [dadosUsuario, navigate]); // O useEffect depende de usuarioLogado e navigate para garantir que a verificação seja feita sempre que o componente for renderizado ou quando o estado do usuário logado mudar.
    

// Esta função salvarDadosArtigo é responsável por salvar os dados do artigo de forma local, utilizando o local storage do navegador. Ela é acionada quando o usuário clica no botão "Salvar" no formulário de cadastro de artigo. A função cria um objeto com os dados do artigo (título, data de publicação e conteúdo) e os salva no local storage em formato JSON. Além disso, a função ativa um componente de mensagem (toast) para fornecer feedback ao usuário de que o rascunho do artigo foi salvo com sucesso. O toast é exibido por um período determinado (neste caso, 5 segundos) antes de ser ocultado automaticamente. Essa funcionalidade permite que os usuários salvem seus rascunhos localmente e continuem editando posteriormente sem perder as informações já inseridas.
const salvarDadosArtigo = () => {
        const dadosArtigo = {
            titulo,
            dataPublicacao,
            conteudo
        };
        console.log("Dados do artigo a serem salvos:", dadosArtigo);
        // Aqui você pode implementar a lógica para salvar os dados do artigo, seja enviando para o backend ou armazenando localmente.

        localStorage.setItem("DadosArtigo", JSON.stringify(dadosArtigo));
        //exibirToast("Rascunho do artigo salvo no navegador local. Para continuar escrevendo use o mesmo dispositivo.");

        //Ativa o Toast para exibir a mensagem de feedback ao usuário
        setShowToast(true); 
        // Opcional: Esconder automaticamente após 3 segundos
        setTimeout(() => setShowToast(false), 5000);
}


/* Esta função publicarArtigo é responsável por enviar os dados do artigo para o backend para serem salvos no banco de dados e publicados no site. Ela é acionada quando o usuário clica no botão "Publicar" no formulário de cadastro de artigo. A função realiza as seguintes etapas:
1. Impede o recarregamento da página e a "sujeira" na URL usando evento.preventDefault().
2. Cria um objeto FormData para montar os dados do artigo, incluindo os campos de texto (título, data de publicação e conteúdo) e o arquivo de capa, se houver um arquivo selecionado.
3. Recupera o token de autenticação do local storage para incluir no cabeçalho da requisição, caso o usuário esteja logado.
4. Envia os dados do artigo para a API usando fetch, com o método POST e os dados do FormData.
5. Trata a resposta da API, exibindo mensagens de sucesso ou erro para o usuário com base no status da resposta.
6. Em caso de sucesso, limpa os campos do formulário e redireciona para a página de resultado com uma mensagem de sucesso.
7. Em caso de erro, exibe a mensagem de erro retornada pela API para que o usuário possa corrigir*/
const publicarArtigo = async (evento) => {
    console.log("Publicando artigo com os seguintes dados:");
        // Essencial: Impede o recarregamento da página e a "sujeira" na URL
        evento.preventDefault();
        setCarregando (true); // Trava o botão cadastrar durante a requisição
        // Limpa mensagens anteriores
        setMensagem({ texto: '', tipo: '' });

        
        console.log("DEBUG ANTES DO ENVIO - Titulo:", titulo); // Se aqui aparecer vazio, o erro é no React
        //  Criar o "envelope" FormData. Monta o objeto com os estados.
        const formData = new FormData();
    

       if (artigo && artigo.id && artigo.uuid) {
            formData.append('idArtigo', artigo.id); // Se estivermos editando um artigo existente, incluímos o ID do artigo no FormData para que o backend saiba que é uma edição e não uma criação de novo artigo. O ID é necessário para identificar qual artigo deve ser atualizado no banco de dados. Se estivermos criando um novo artigo, o ID não será incluído, e o backend entenderá que deve criar um novo registro no banco de dados.
            formData.append('uuidArtigo', artigo.uuid); // Adiciona o UUID do artigo ao FormData. Enviar a uuid para o formdata é importante para tratar no backend a manipulação do arquivo de capa que é baseado na uuid do artigo. 
        }
        
        
        
        // . Adicionar os campos de texto ao FormData
        formData.append('titulo', titulo);
        formData.append('dataPublicacao', dataPublicacao);
        formData.append('conteudo', conteudo); // O conteúdo do editor já está em formato HTML, então pode ser enviado diretamente

        // . Adicionar o arquivo de capa ao FormData, se houver um arquivo selecionado
        if (capaArtigo) {
            formData.append('capa', capaArtigo);
        }

        
        try{

            // Usa a variável de ambiente que agora está funcionando
            // O React substitui isso em tempo de execução. A variável baseUrl conterá a URL da API definida no arquivo .env

            

            const endpoint = !artigo ? '/artigo-new' : `/artigo-edit`; // Define o endpoint com base no estado do artigo, se tivermos criando um novo artigo ou editando um existente. Para um novo artigo, o endpoint é '/artigo-new', enquanto para edição de um artigo existente, o endpoint seria algo como '/artigo-edit/' onde o {id} é o identificador do artigo que está sendo editado e será enviado dentro do formData.    
            const url =  `${process.env.REACT_APP_API_URL}${endpoint}`;

            // Configura os cabeçalhos da requisição, incluindo o token de autenticação se estiver disponível
            const headers = {
                // 'Content-Type': 'multipart/form-data', // Importante: Não definimos o 'Content-Type' manualmente para FormData. O navegador fará isso automaticamente, incluindo os limites necessários para multipart/form-data.
            };      

            // Se houver um token (usuário logado editando), adicionamos ao cabeçalho
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            // Envia os dados do usuário para a API
            const resposta = await fetch(url, {  
                method: 'POST',
                headers: headers,
                body: formData/* Enviamos o FormData direto, sem JSON.stringify
                                IMPORTANTE: Não definimos o 'Content-Type' manualmente aqui.  
                                O navegador fará isso automaticamente para multipart/form-data */
              });

              if (resposta.ok) {
                const dadosRetorno = await resposta.json();
                if (dadosRetorno.status === 'sucesso') {
                    setMensagem({ texto: dadosRetorno.mensagem, tipo: dadosRetorno.status });
                    // Limpa os campos após o envio se for inclusão, caso contrário, mantém os dados para edição

                    //Passamos o state para a página de resultado usando o navigate do react-router-dom, que permite enviar um objeto de estado junto com a navegação. Assim, a página de resultado pode acessar esse estado para exibir a mensagem apropriada. Só usaremos a página resultado para exibir mensagens de sucesso. Erros serão exibidos na própria página de cadastro para que o usuário possa corrigir sem perder os dados já preenchidos.
                    navigate('/resultado', { 
                          state: { 
                            status: dadosRetorno.status,
                            mensagem: dadosRetorno.mensagem
                          }
                        });

                        setCapaArtigo(null); // Limpa o estado do arquivo de capa após o envio bem-sucedido
                        setTitulo(''); // Limpa o estado do título após o envio bem-sucedido
                        setDataPublicacao(''); // Limpa o estado da data de publicação após o envio bem-sucedido
                        setConteudo(''); // Limpa o estado do conteúdo após o envio bem-sucedido 

                        localStorage.removeItem("DadosArtigo"); // Limpa os dados do artigo do local storage após o envio bem-sucedido, caso existam dados salvos para edição

                    setCarregando (false); // Destrava o botão cadastrar após o processamento da requisição
                    return;

                }else {

                    // Se o erro for 401, significa que o token expirou durante a edição
                    if (resposta.status === 401) {
                        localStorage.removeItem('viver_token');
                        navigate('/login', { state: { mensagem: "Sessão expirada. Logue novamente." } });
                    return;
                    }

                    setMensagem({ texto: dadosRetorno.mensagem, tipo: dadosRetorno.status });
                    setCarregando (false); // Destrava o botão cadastrar após o processamento da requisição
                  return;
                } 
              }else {
                /// Aqui o PHP enviou um erro proposital (ex: 400 Bad Request)
                const erroRetorno = await resposta.json();
                console.table(erroRetorno);
                setMensagem({ 
                            texto: erroRetorno.mensagem || "Erro ao processar o artigo.", 
                            tipo: erroRetorno.status || 'erro'});
                setCarregando (false); // Destrava o botão cadastrar após o processamento da requisição
             }   

        }catch (error) {
            setMensagem({ texto: error.message, tipo: 'erro' });
            console.error("Erro ao cadastrar artigo:", error);
            setCarregando (false); // Destrava o botão cadastrar após o processamento da requisição
        }
}


    return (
        
        <div className="container mt-5">
            {dadosUsuario && (
                <div>
                    <h1 className="h1">Cadastro de Artigo</h1>
                    <form onSubmit={publicarArtigo} >

                        <div className="row mb-3">
                            <CampoTexto
                                label="Título do Artigo"
                                labelClass="form-label fs-5"
                                className="form-control form-control-lg fs-3"
                                wrapperClass="col-12 col-md-6"
                                tipo="text"
                                placeholder="Digite o título do artigo"
                                obrigatorio={obrigatorio}
                                editavel={editavel}
                                tamanhoMaximo={100}
                                nome="titulo"
                                id="titulo"
                                /*O valor do campo será controlado pelo estado do campo */
                                valor={titulo} 
                                /*Quando o usuário digitar algo, a função setTitulo será chamada para atualizar o estado*/
                                aoDigitado={ valor => setTitulo(valor) } 
                            />
                        </div>

                        <div className="row mb-3">
                            <CampoTexto
                                label="Capa do Artigo"
                                labelClass="form-label fs-5"
                                className="form-control form-control-lg fs-3"
                                wrapperClass="col-12 col-md-6"
                                tipo="file"
                                placeholder="Escolha a capa do artigo. Se estiver editando não é obrigatório selecionar um novo arquivo, a capa atual será mantida se nenhum novo arquivo for selecionado."
                                obrigatorio={!artigo} // O campo de capa é obrigatório apenas para novos artigos, não para edição, pois o artigo já pode ter uma capa existente. Se estivermos editando um artigo existente, o campo de capa não será obrigatório, permitindo que o usuário mantenha a capa atual sem precisar selecionar um novo arquivo. Se estivermos criando um novo artigo, o campo de capa será obrigatório para garantir que o usuário selecione uma imagem para a capa do artigo.   
                                editavel={editavel}
                                tamanhoMaximo={100}
                                nome="capa"
                                id="capa"
                                /*O valor do campo será controlado pelo estado do campo */
                                onChange={tratarArquivo}
                            />
                        </div>

                        <div className="row mb-3">
                            <CampoTexto
                                label="Data de Publicação"
                                labelClass="form-label fs-5"
                                className="form-control fs-3"
                                wrapperClass="col-4" // Define a largura do campo para 4 colunas
                                tipo="datetime-local"
                                placeholder="Selecione a data de publicação"
                                obrigatorio={obrigatorio}
                                editavel={dadosUsuario.tipo === 'admin'} // Apenas administradores podem editar a data de publicação, enquanto editores podem apenas visualizar
                                nome="dataPublicacao"
                                id="dataPublicacao"
                                /*O valor do campo será controlado pelo estado do campo */
                                valor={dataPublicacao} 
                                /*Quando o usuário digitar algo, a função setDataPublicacao será chamada para atualizar o estado*/
                                aoDigitado={ valor => setDataPublicacao(valor) } 
                            />
                        </div>

                        {/* A div abaixo vai acomodar dois botões com funcionalidades diferentes. 
                            - Botão Salvar - salva os dados do artigo de forma local, no local storage, para que o usuário possa recuperá-los antes de publicá-lo
                            - Botão Publicar - envia os dados do artigo para o backend para serem salvos no banco de dados e publicados no site.
                        */}

                        <Mensagem mensagem={mensagem.texto} tipo={mensagem.tipo} />
                        
                        <div className="toast-container position-fixed bottom-0 end-0 p-3">
                            <div 
                                id="liveToast"
                                //Aqui neste ponto a classe do toast é definida dinamicamente com base no estado showToast. Se showToast for true, a classe 'show' será adicionada, tornando o toast visível. Se showToast for false, a classe 'hide' será aplicada, ocultando o toast. Isso permite controlar a exibição do toast de forma reativa com base no estado do componente. Funcionamento do Toast: https://getbootstrap.com/docs/5.3/components/toasts/#how-it-works    
                                className={`toast ${showToast ? 'show' : 'hide'}`} 
                                role="alert" 
                                aria-live="assertive" 
                                aria-atomic="true"
                            >
                                <div className="toast-header">
                                    <strong className="me-auto">Viver Espirita</strong>
                                    <button 
                                        type="button" 
                                        className="btn-close"
                                        data-bs-dismiss="toast"
                                        aria-label="Close">
                                    </button>
                                </div>
                                <div className="toast-body">
                                    Rascunho do artigo salvo.
                                </div>
                            </div>
                        </div>

                        {/*Container para o editor de texto rico*/}
                        <div className="row mb-3">
                            
                                <div className="editor-container">
                                    {/* BARRA FIXA DE BOTÕES */}
                                        <div className="painel-acoes-sticky d-flex align-items-center justify-content-start p-2 bg-light border border-bottom-0 rounded-top">
                                            <Botao
                                                className="btn btn-primary btn-lg me-5"
                                                tipo="button"
                                                aoClicar={salvarDadosArtigo}
                                            >
                                                Salvar
                                            </Botao>
                                        
                                            <Botao
                                                className="btn btn-success btn-lg me-5"
                                                tipo="submit"
                                                disabled={carregando} // Desabilita o botão enquanto a requisição está em andamento para evitar múltiplos cliques e envios acidentais
                                                
                                            >
                                                {carregando ? 'Publicando...' : 'Publicar'} {/* Exibe "Publicando..." enquanto a requisição está em andamento, caso contrário, exibe "Publicar" */}    
                                            </Botao>
                                        </div>
                                    
                                    <ReactQuill
                                        theme="snow" 
                                        value={conteudo} 
                                        onChange={setConteudo} // O Quill passa o HTML direto aqui
                                        modules={modules}
                                        placeholder="Escreva a sabedoria de hoje..."
                                    />
                                </div>
                            </div>    
                    </form>
                </div>
            )}
        </div>
    )
}

export default CadastroArtigo